import type { ColumnDef } from '@tanstack/react-table';
import { createSerialColumn, StackedCellText } from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { MeetPeopleCell } from '@/components/common/MeetPeopleCell';
import { cn } from '@/lib/utils';
import { CODE_BADGE_CLASS } from '@/styles/codeBadge';
import { formatDisplayDate, formatTime } from '@/utils/date';
import { OPTION_DETAIL_SEPARATOR } from '@/utils/filters';
import { AreaRouteCell } from './AreaRouteCell';
import {
  DcrDecisionCell,
  DcrFareCell,
  DcrTransportCell,
  type DcrQueueColumnDeps,
} from './DcrQueueCells';
import { getDistributorNameColumn } from './DistributorColumns';
import { getQtyColumn } from './QtyColumn';
import { WorkTypeCell } from './WorkTypeCell';
import { QUEUE_DECISION_COLUMN_ID } from '../constants/billing.constants';
import type { OfflineDcrRow } from '../types/billing.types';

export function getDcrQueueColumns(deps: DcrQueueColumnDeps): ColumnDef<OfflineDcrRow, unknown>[] {
  return [
    createSerialColumn<OfflineDcrRow>(),
    getDistributorNameColumn<OfflineDcrRow>(deps.searchText, (row) =>
      row.onBehalfOf ? `on behalf ${row.onBehalfOf}` : undefined,
    ),
    {
      id: 'institute',
      header: 'Institution / Location',
      cell: ({ row }) => {
        const { instituteName, instituteNo } = row.original;
        const area = [
          { label: 'Thana', value: row.original.instituteThana },
          { label: 'District', value: row.original.instituteDistrict },
          { label: 'Division', value: row.original.instituteDivision },
        ].filter((part) => Boolean(part.value));
        const codeBadge =
          instituteNo === null ? null : (
            <span className={cn(CODE_BADGE_CLASS, 'ml-2 align-middle')}>{instituteNo}</span>
          );

        return (
          <StackedCellText
            primary={
              instituteName ? (
                <>
                  {instituteName}
                  {codeBadge}
                </>
              ) : (
                <EmptyValue />
              )
            }
            secondary={area
              .map((part) => `${part.label}: ${part.value}`)
              .join(OPTION_DETAIL_SEPARATOR)}
          />
        );
      },
    },
    {
      id: 'contact',
      header: 'Meet Person Cell',
      cell: ({ row }) => <MeetPeopleCell meetPeople={row.original.meetPeople} />,
    },
    {
      id: 'route',
      header: 'Area From – To',
      cell: ({ row }) => (
        <AreaRouteCell
          fromName={row.original.areaFrom}
          toName={row.original.areaTo}
          comments={row.original.comments}
          userId={row.original.userId}
          personName={row.original.name}
          dcrDate={row.original.dcrDate}
        />
      ),
    },
    {
      id: 'time',
      header: 'Date / Time',
      cell: ({ row }) => (
        <StackedCellText
          primary={formatDisplayDate(row.original.dcrDate)}
          secondary={`${formatTime(row.original.timeFrom)} – ${formatTime(row.original.timeTo)}`}
        />
      ),
    },
    {
      id: 'workType',
      header: 'Work Type',
      cell: ({ row }) => (
        <WorkTypeCell
          code={row.original.workTypeCode}
          name={row.original.workType}
          onOpenDetails={() => deps.onOpenDetails(row.original.recordId)}
        />
      ),
    },
    getQtyColumn(),
    {
      id: 'transport',
      header: 'Transport Type',
      cell: ({ row }) => <DcrTransportCell row={row.original} deps={deps} />,
    },
    {
      id: 'fare',
      header: 'Fare (Tk)',
      cell: ({ row }) => <DcrFareCell row={row.original} deps={deps} />,
    },
    {
      id: QUEUE_DECISION_COLUMN_ID,
      header: 'Status',
      cell: ({ row }) => <DcrDecisionCell row={row.original} deps={deps} />,
    },
  ];
}
